/**
 * Labels PT-BR compartilhados do feature Supervisor (fidelidade Brief 3 —
 * enum cru nunca chega ao operador; eixo 5 do passe de fidelidade).
 */
import type { Severity, SequenceFlag } from './view-models';

/** Severidade (ConflictAlert / padrão comportamental) em PT minúsculo. */
export const SEVERITY_LABELS: Record<Severity, string> = {
  critical: 'crítico',
  high: 'alto',
  medium: 'médio',
  low: 'baixo',
};

/** Label PT de severidade a partir de string crua (fallback: o próprio valor). */
export function severityLabel(severity: string): string {
  return SEVERITY_LABELS[severity as Severity] ?? severity;
}

/** Flags canônicas de ConversationSequence (ADR-055) em PT. */
export const SEQUENCE_FLAG_LABELS: Record<SequenceFlag, string> = {
  highVolume: 'alto volume',
  escalating: 'escalando',
  allAbandoned: 'abandonadas',
  crossChannel: 'multicanal',
};

/**
 * Duração compacta a partir de segundos: "42s" · "35min" · "4h 12min" · "9h".
 * Faixas curtas espelham o design (SLA em segundos nos cards); horas humanizadas
 * substituem os "553min"/"34582s" ilegíveis com dado real.
 */
export function formatDurationShort(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  // Até 2min em segundos (design mostra "42s"/"78s"/"152s" — precisão importa em SLA curto).
  if (s < 120) return `${String(s)}s`;
  const totalMin = Math.round(s / 60);
  if (totalMin < 60) return `${String(totalMin)}min`;
  const h = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  return min === 0 ? `${String(h)}h` : `${String(h)}h ${String(min)}min`;
}

/**
 * Labels PT dos 21 BondTypes canônicos (ADR-037/131) — espelha o mapa do
 * backend (supervisor-dashboard-aggregator BOND_TYPE_LABELS).
 */
export const BOND_LABELS: Record<string, string> = {
  contract: 'Contrato',
  customer_account: 'Conta de cliente',
  legal_case: 'Caso jurídico',
  mediation: 'Mediação',
  compliance_case: 'Caso de compliance',
  patient_care: 'Atendimento ao paciente',
  student: 'Estudante',
  social_care_case: 'Caso de assistência social',
  home_care: 'Cuidado domiciliar',
  property: 'Imóvel',
  vehicle: 'Veículo',
  equipment: 'Equipamento',
  partnership: 'Parceria',
  vendor_relationship: 'Fornecedor',
  opportunity: 'Oportunidade',
  procurement: 'Aquisição',
  project: 'Projeto',
  interest: 'Interesse',
  purchase_intent: 'Intenção de compra',
  transaction_attempt: 'Tentativa de transação',
  rental_contract: 'Contrato de aluguel',
};

/** Humaniza um BondType cru; fallback prettifica snake_case → Title Case. */
export function bondLabelOf(kind: string): string {
  return (
    BOND_LABELS[kind] ??
    kind
      .split('_')
      .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
      .join(' ')
  );
}
